import { useCallback, useEffect, useMemo, useState } from "react";
import { IconArrowsVertical, IconCircleCheck, IconExternalLink } from "@tabler/icons-react";
import { axiosInstance } from "../../utils/axiosInstance";
import { CmmsOpenWorkOrder } from "../../interfaces/interfaces";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  CollapsibleFilters,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  Pill,
  RefreshButton,
  Tone,
} from "../ui";

function formatWhen(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function isOverdue(dueDate?: string | null): boolean {
  if (!dueDate) return false;
  const t = new Date(String(dueDate)).getTime();
  return Number.isFinite(t) && t < Date.now();
}

function statusLabel(wo: CmmsOpenWorkOrder): string {
  if (wo.status == null || wo.status === "") return "";
  return String(wo.status);
}

function priorityLabel(wo: CmmsOpenWorkOrder): string {
  if (wo.priority == null || wo.priority === "") return "";
  return String(wo.priority);
}

function priorityTone(label: string): Tone {
  const p = label.toLowerCase();
  if (p.includes("urgent") || p.includes("critical") || p.includes("high")) return "bad";
  if (p.includes("med")) return "warn";
  return "neutral";
}

function sortByDue(
  list: CmmsOpenWorkOrder[],
  ascending: boolean
): CmmsOpenWorkOrder[] {
  return list.slice().sort((a, b) => {
    const ta = a.dueDate ? new Date(String(a.dueDate)).getTime() : 0;
    const tb = b.dueDate ? new Date(String(b.dueDate)).getTime() : 0;
    if (ta !== tb) return ascending ? ta - tb : tb - ta;
    const ia = Number(a.id) || 0;
    const ib = Number(b.id) || 0;
    return ascending ? ia - ib : ib - ia;
  });
}

export default function OpenIssuesHome() {
  const [workOrders, setWorkOrders] = useState<CmmsOpenWorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [dateAscending, setDateAscending] = useState(false);
  const [siteFilter, setSiteFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const res = await axiosInstance.get<{
        workOrders: CmmsOpenWorkOrder[];
        message?: string;
      }>("/cmms/open-work-orders");
      const list = res.data.workOrders || [];
      setWorkOrders(list);
      if (res.data.message && list.length === 0) {
        setInfo(res.data.message);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      if (status === 400 && Array.isArray(data?.workOrders)) {
        setWorkOrders(data.workOrders);
        setInfo(data.message || null);
        setError(null);
      } else {
        const msg =
          data?.message ||
          err?.message ||
          "Failed to load work orders.";
        setError(msg);
        setWorkOrders([]);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const siteOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const wo of workOrders) {
      if (wo.speedQueenLocationId == null) continue;
      const id = String(wo.speedQueenLocationId);
      if (!map.has(id)) {
        map.set(id, wo.speedQueenLocationName || `Site ${id}`);
      }
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [workOrders]);

  const priorityOptions = useMemo(() => {
    const set = new Set<string>();
    for (const wo of workOrders) {
      const p = priorityLabel(wo);
      if (p) set.add(p);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [workOrders]);

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    for (const wo of workOrders) {
      const s = statusLabel(wo);
      if (s) set.add(s);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [workOrders]);

  const display = useMemo(() => {
    const filtered = workOrders.filter((wo) => {
      if (siteFilter && String(wo.speedQueenLocationId ?? "") !== siteFilter) {
        return false;
      }
      if (priorityFilter && priorityLabel(wo) !== priorityFilter) return false;
      if (statusFilter && statusLabel(wo) !== statusFilter) return false;
      return true;
    });
    return sortByDue(filtered, dateAscending);
  }, [workOrders, siteFilter, priorityFilter, statusFilter, dateAscending]);

  const filtersActive = Boolean(siteFilter || priorityFilter || statusFilter);

  const clearFilters = () => {
    setSiteFilter("");
    setPriorityFilter("");
    setStatusFilter("");
  };

  return (
    <PageContainer>
      <PageHeader
        title="Open issues"
        subtitle="Work orders from Nitro CMMS for mapped locations"
        actions={
          <>
            <a
              className="btn btn-sm btn-outline-secondary"
              href="https://docs.nitrocmms.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Nitro CMMS docs
            </a>
            <RefreshButton onClick={load} loading={loading} />
          </>
        }
      />

      {!loading && error ? <ErrorBanner onRetry={load}>{error}</ErrorBanner> : null}
      {!loading && !error && info && workOrders.length === 0 ? (
        <div className="alert alert-info" role="alert">
          {info}
        </div>
      ) : null}

      <Card flush>
        {!loading && !error && workOrders.length > 0 ? (
          <div className="sq-toolbar">
            <CollapsibleFilters
              activeCount={[siteFilter, priorityFilter, statusFilter].filter(Boolean).length}
            >
              <label className="sq-field">
                <span>Site</span>
                <select
                  className="sq-input"
                  value={siteFilter}
                  onChange={(e) => setSiteFilter(e.target.value)}
                >
                  <option value="">All sites</option>
                  {siteOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="sq-field is-half">
                <span>Priority</span>
                <select
                  className="sq-input"
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {priorityOptions.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </label>
              <label className="sq-field is-half">
                <span>Status</span>
                <select
                  className="sq-input"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <span className="sq-count sq-toolbar-end">
                Showing {display.length} of {workOrders.length}
              </span>
            </CollapsibleFilters>
          </div>
        ) : null}

        {loading ? <LoadingPanel label="Loading work orders" rows={7} /> : null}

        {!loading && !error && workOrders.length > 0 && display.length === 0 ? (
          <EmptyState
            title="No work orders match these filters"
            action={
              filtersActive ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              ) : undefined
            }
          />
        ) : null}

        {!loading && !error && display.length > 0 ? (
          <div className="sq-table-wrap">
            <table className="sq-table is-stacked">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Title</th>
                  <th scope="col">Site</th>
                  <th scope="col">Status</th>
                  <th scope="col">Priority</th>
                  <th scope="col" aria-sort={dateAscending ? "ascending" : "descending"}>
                    <button
                      type="button"
                      className="sortable"
                      onClick={() => setDateAscending((prev) => !prev)}
                    >
                      Due
                      <IconArrowsVertical size={14} stroke={1.75} />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {display.map((wo) => {
                  const overdue = isOverdue(wo.dueDate ? String(wo.dueDate) : null);
                  const href =
                    typeof wo.externalUrl === "string" && wo.externalUrl ? wo.externalUrl : null;
                  const priority = priorityLabel(wo);
                  return (
                    <tr
                      key={`${wo.nitroCmmsLocationId}-${wo.id}`}
                      className={overdue ? "is-danger" : undefined}
                    >
                      <td data-label="ID" className="nowrap">
                        {href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sq-text-link d-inline-flex align-items-center gap-1"
                            title="Open in Nitro CMMS"
                          >
                            {wo.id ?? "—"}
                            <IconExternalLink size={14} stroke={1.75} />
                          </a>
                        ) : (
                          wo.id ?? "—"
                        )}
                      </td>
                      <td className="cell-title">
                        <div className="sq-strong">
                          {href ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="sq-text-link"
                              title="Open in Nitro CMMS"
                            >
                              {wo.title || "—"}
                            </a>
                          ) : (
                            wo.title || "—"
                          )}
                        </div>
                        {wo.description ? (
                          <div className="sq-muted sq-clamp">{String(wo.description)}</div>
                        ) : null}
                      </td>
                      <td data-label="Site">{wo.speedQueenLocationName || "—"}</td>
                      <td data-label="Status">{statusLabel(wo) || "—"}</td>
                      <td data-label="Priority">
                        {priority ? <Pill tone={priorityTone(priority)}>{priority}</Pill> : "—"}
                      </td>
                      <td data-label="Due">
                        <div>{formatWhen(wo.dueDate ? String(wo.dueDate) : null)}</div>
                        {overdue ? (
                          <div className="mt-1">
                            <Pill tone="bad">Overdue</Pill>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {!loading && !error && !info && workOrders.length === 0 ? (
          <EmptyState icon={<IconCircleCheck size={24} />} title="No open work orders">
            Configure the API key under Settings → CMMS and map Nitro location IDs on each store.
          </EmptyState>
        ) : null}
      </Card>
    </PageContainer>
  );
}
